/**
 * Offline Queue Manager
 * Stores failed API requests when offline and retries them when back online
 */

const QUEUE_STORAGE_KEY = 'offlineRequestQueue';
const MAX_QUEUE_SIZE = 100; // Prevent localStorage from getting too large

/**
 * Get the current queue from localStorage
 */
export const getQueue = () => {
  try {
    const queueStr = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!queueStr) return [];
    return JSON.parse(queueStr);
  } catch (error) {
    console.error('Error reading offline queue:', error);
    return [];
  }
};

/**
 * Save the queue to localStorage
 */
export const saveQueue = (queue) => {
  try {
    // Limit queue size to prevent localStorage issues
    const limitedQueue = queue.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(limitedQueue));
    return limitedQueue;
  } catch (error) {
    console.error('Error saving offline queue:', error);
    // If storage is full, try to clear old items
    if (error.name === 'QuotaExceededError') {
      const reducedQueue = queue.slice(-Math.floor(MAX_QUEUE_SIZE / 2));
      try {
        localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(reducedQueue));
        return reducedQueue;
      } catch (retryError) {
        console.error('Error saving reduced queue:', retryError);
        return [];
      }
    }
    return queue;
  }
};

/**
 * Add a request to the queue
 */
export const addToQueue = (request) => {
  const queue = getQueue();
  const newRequest = {
    ...request,
    id: request.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Date.now(),
    retryCount: request.retryCount || 0,
  };
  queue.push(newRequest);
  return saveQueue(queue);
};

/**
 * Remove a request from the queue by ID
 */
export const removeFromQueue = (requestId) => {
  const queue = getQueue();
  const filteredQueue = queue.filter((req) => req.id !== requestId);
  return saveQueue(filteredQueue);
};

/**
 * Clear the entire queue
 */
export const clearQueue = () => {
  try {
    localStorage.removeItem(QUEUE_STORAGE_KEY);
    return [];
  } catch (error) {
    console.error('Error clearing offline queue:', error);
    return [];
  }
};

/**
 * Get queue size
 */
export const getQueueSize = () => {
  return getQueue().length;
};

/**
 * Check if we're online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Check if error is due to being offline
 */
export const isOfflineError = (error) => {
  if (!error) return false;

  // Network error or no response
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
    return true;
  }

  // No internet connection
  if (!navigator.onLine) {
    return true;
  }

  // Timeout or connection refused
  if (error.message?.includes('timeout') || error.message?.includes('Network Error')) {
    return true;
  }

  return false;
};
